import {get, patch, post} from './apiClient'
import {ENDPOINTS} from '../utils/constants/apiEndpoints'

// AI Metrics interfaces based on Angular implementation
export interface AIMetricsData {
  productDashboardId: string
  productName: string
  yearMonth: string
  totalAiUserStories: number
  totalUserStories: number
  aiAdoptionRate: number
  productivityGainPercentage: number
  totalAiAssistedUserStoryPointSaving: number
  totalUserStoryPoints: number
  hourToStoryPointRatio: number
  hoursSaved?: number // API now provides this directly
  totalHours?: number // API now provides this directly
}

export interface AIMetricsResponse {
  data: AIMetricsData[]
}

export interface AIMetricsTableData {
  repo: {
    name: string
    id: string
  }
  'AI ADOPTION': string | number
  'PRODUCTIVITY GAINS': string | number
  'SP SAVED': string | number
  'TOTAL EFFORT (SP)': string | number
  'HOURS SAVED': string | number
  'TOTAL HOURS': string | number
}

export interface AIMetricsParams {
  productDashboardId: string
  month: number
  year: number
}

// Define query parameters interface for AI Productivity Hierarchical
export interface AIProductivityHierarchicalParams {
  year: number
  month: number
  rootEmployeeEmail: string
  programIds?: string[]
  isDeveloper?: boolean // Optional: true for Developer, false for Non-Developer, undefined for All
  isFullTimeEmployee?: boolean // Optional: true for Full-time, false for Contractor, undefined for All
  level?: number // Optional: depth of reportees to fetch (default: 1 for lazy loading)
  programAnalyticsType?: number // Optional: maps to `program-analytics-type`
}

/** Tool filter values accepted by the agentic-analytics APIs */
export const AI_TOOL = {ALL: 0, WINDSURF: 1, COPILOT: 2} as const
export type AIToolFilter = (typeof AI_TOOL)[keyof typeof AI_TOOL]

type QueryParams = Record<string, string | number | boolean>

const periodUrl = (year: number, month: number, resource: string): string =>
  `${ENDPOINTS.AI_AGENTIC_ANALYTICS}/${year}/${month}/${resource}`

const setProgramFilters = (query: QueryParams, programIds?: string[], programAnalyticsType?: number): void => {
  if (programIds?.length) query.programIds = programIds.join(',')
  if (programAnalyticsType !== undefined) query['program-analytics-type'] = programAnalyticsType
}

// Params shared by the new employee-type / role based agentic-analytics endpoints
export interface AIEmployeeScopeParams {
  email: string
  employeeType?: number
  role?: number
  programIds?: string[]
  programAnalyticsType?: number
}

export interface InactiveUsersListItem {
  gpid: string
  email: string
  name: string
  managerName?: string
  tools: {windsurf?: {inActive: boolean}; copilot?: {inActive: boolean}}
}

export interface InactiveUsersListResponse {
  data: InactiveUsersListItem[]
  page?: number
  size?: number
  total?: number
  totalPages?: number
}

export interface ToolMetricsResponse {
  toolName?: string
  activeUsers?: number
  licensedUsers?: number
  unutilizedLicenses?: number
  [key: string]: unknown
}

export interface AggregateMetricsResponse {
  metricsOverview?: Record<string, number>
  linesOfCodeOverview?: {percentageOfManualCodeLines?: string; percentageOfAiCodeLines?: string}
  creditOverview?: {creditUsed?: number; creditAllocated?: number; creditUseaPercentage?: number}
  [key: string]: unknown
}

export interface AIPeriodScopeParams extends AIEmployeeScopeParams {
  year: number
  month: number
}

export type AIUserSearchParams = AIEmployeeScopeParams

export interface AIInactiveUsersListParams extends AIPeriodScopeParams {
  tool?: AIToolFilter
  page?: number
  size?: number
}

export interface AIToolPeriodParams {
  year: number
  month: number
  email: string
  tool?: AIToolFilter
  programIds?: string[]
  programAnalyticsType?: number
}

// Define query parameters interface for AI Productivity Monthly Trend Range
export interface AIProductivityMonthlyTrendRangeParams {
  startDate: string // Format: YYYY/MM
  endDate: string // Format: YYYY/MM
  rootEmployeeEmail: string
  programIds?: string[]
  isDeveloper?: boolean // Optional: true for Developer, false for Non-Developer, undefined for All
  isFullTimeEmployee?: boolean // Optional: true for Full-time, false for Contractor, undefined for All
  programAnalyticsType?: number
}

// Define query parameters interface for AI Productivity Reportees
export interface AIProductivityReporteesParams {
  rootEmployeeEmail: string
  level?: number
}

// Define parameters interface for access delegations audit
export interface AccessDelegationsAuditParams {
  capability: string
  page: number
  size: number
  startDate?: string
  endDate?: string
  search?: string
}

// Define parameters interface for access delegations candidates
export interface AccessDelegationsCandidatesParams {
  page: number
  size: number
  search?: string
}

// Define parameters interface for updating user settings
export interface UpdateUserSettingsParams {
  email: string
  isDeveloper?: boolean
  isLicRevExcl?: boolean
  licenseRevocationExclusions?: string[]
}

// Define query parameters interface for AI Productivity Inactive Users
export interface AIProductivityInactiveUsersParams {
  year: number
  month: number
  rootEmployeeEmail: string
  programIds?: string[]
  isDeveloper?: boolean // Optional: true for Developer, false for Non-Developer, undefined for All
  isFullTimeEmployee?: boolean // Optional: true for Full-time, false for Contractor, undefined for All
  isDetailView?: boolean // Optional: false for summary data only, true for detailed inactive users list
}

// Define query parameters interface for Credit Usage
export interface CreditUsageParams {
  year: number
  month: number
  rootEmployeeEmail: string
  programIds?: string[]
  page?: number
  size?: number
  isDetailView?: boolean
  toolTypes?: string[] // e.g. ['windsurf', 'copilot'] or omit for all
  isDeveloper?: boolean
  isFullTimeEmployee?: boolean
  programAnalyticsType?: number
}

// Define query parameters interface for AI LOC Benchmark
export interface AILoCBenchmarkParams {
  year: number
  month: number
  page?: number
  size?: number
  isFullTimeEmployee?: boolean
  isDeveloper?: boolean
  rootEmployeeEmail?: string // sent as the `email` query param
  programIds?: string[]
  programAnalyticsType?: number
}

// Define interface for AI LOC Benchmark contributor
export interface AILoCBenchmarkContributor {
  name: string
  teamSize: number
  aiAssistedloc: number
}

// Define interface for AI LOC Benchmark response
export interface AILoCBenchmarkResponse {
  total: number
  page: number
  size: number
  contributors: AILoCBenchmarkContributor[]
  isManager: boolean
}

// Define query parameters interface for User Hierarchy
export interface UserHierarchyParams {
  email?: string // Optional email parameter
}

// Define query parameters interface for AI Performer
export interface AIPerformerParams {
  year: number
  month: number
  toolTypes?: string // Comma-separated tool types: 'windsurf', 'copilot', 'all', or 'windsurf,copilot'
  selectedTab?: string // 'loc' for Lines of Code or 'engagement' for Engagement Ratio
}

// Define query parameters interface for Credit Usage Monthly Trend
export interface CreditUsageMonthlyTrendParams {
  rootEmployeeEmail: string
  programIds?: string[]
  isDeveloper?: boolean
  startDate?: string
  endDate?: string
  isFullTimeEmployee?: boolean
  programAnalyticsType?: number
}

// Define query parameters interface for LOC Usage Monthly Trend
export interface LocUsageMonthlyTrendParams {
  rootEmployeeEmail: string
  programIds?: string[]
  isDeveloper?: boolean
  startDate?: string
  endDate?: string
  isFullTimeEmployee?: boolean
  toolTypes: string[] // Array of tool types: e.g. ['windsurf'], ['copilot'], or ['windsurf', 'copilot']
  programAnalyticsType?: number
}

// Define interface for inactive user data
export interface InactiveUser {
  gpid: string
  email: string
  name: string
  inActiveForWindsurf: boolean
  inActiveForCopilot: boolean
}

// Define interface for inactive users response
export interface InactiveUsersResponse {
  gpid: string
  email: string
  name: string
  inActiveForWindsurf: boolean
  inActiveForCopilot: boolean
  totalInactiveUsersForWindsurf: number
  totalInactiveUsersForCopilot: number
  totalLicensedUserForWindsurf: number
  totalLicensedUserForCopilot: number
  overallLicenseLeakage: number
  inactiveUsers: InactiveUser[]
}

/**
 * AI Metrics Service for fetching AI productivity metrics data
 */
class AIMetricsService {
  /**
   * Fetch AI productivity metrics data from the API
   * @param params - Parameters including productDashboardId, month, and year
   * @returns Promise of AIMetricsData array
   */
  async fetchAIMetrics(params: AIMetricsParams): Promise<AIMetricsData[]> {
    try {
      const response = await get(ENDPOINTS.AI_METRICS, {
        params: {
          productDashboardId: params.productDashboardId,
          month: params.month.toString(),
          year: params.year.toString(),
        },
      })

      // Handle response format - similar to Angular implementation
      if (response?.data) {
        if (Array.isArray(response.data)) {
          return response.data as AIMetricsData[]
        } else {
          // Response is a single object, wrap it in an array
          return [response.data as AIMetricsData]
        }
      }

      return []
    } catch (error) {
      console.error('Error fetching AI metrics:', error)
      throw error
    }
  }

  /**
   * Fetch AI metrics for multiple product dashboards
   * @param productDashboardIds - Array of product dashboard IDs
   * @param month - Selected month
   * @param year - Selected year
   * @returns Promise of AIMetricsData array
   */
  async fetchAIMetricsForMultipleDashboards(
    productDashboardIds: string[],
    month: number,
    year: number,
  ): Promise<AIMetricsData[]> {
    try {
      // Create promises for all API calls
      const promises = productDashboardIds.map(dashboardId =>
        this.fetchAIMetrics({
          productDashboardId: dashboardId,
          month,
          year,
        }),
      )

      // Wait for all API calls to complete
      const results = await Promise.all(promises)

      // Flatten the results array
      return results.flat()
    } catch (error) {
      console.error('Error fetching AI metrics for multiple dashboards:', error)
      throw error
    }
  }

  /**
   * Process AI metrics data into table format
   * @param data - Raw AI metrics data
   * @returns Processed table data
   */
  processAIMetricsForTable(data: AIMetricsData[]): AIMetricsTableData[] {
    return data.map(metric => {
      // Calculate hours saved based on story points saved and ratio
      const hoursSaved = metric.totalAiAssistedUserStoryPointSaving * metric.hourToStoryPointRatio
      const totalHours = metric.totalUserStoryPoints * metric.hourToStoryPointRatio

      return {
        repo: {
          name: metric.productName || 'Unknown',
          id: metric.productDashboardId,
        },
        'AI ADOPTION': `${metric.aiAdoptionRate}%`,
        'PRODUCTIVITY GAINS': `${metric.productivityGainPercentage}%`,
        'SP SAVED': metric.totalAiAssistedUserStoryPointSaving,
        'TOTAL EFFORT (SP)': metric.totalUserStoryPoints,
        'HOURS SAVED': hoursSaved.toFixed(2),
        'TOTAL HOURS': totalHours.toFixed(2),
      }
    })
  }

  /**
   * Fetch AI Productivity Hierarchical data for a given year, month, and root employee email
   * URL format: /agentic-analytics/hierarchical/{year}/{month}?rootEmployeeEmail={email}
   * @param params - Parameters including year, month, and rootEmployeeEmail
   * @returns Promise of hierarchical AI productivity data
   */
  async getAIProductivityHierarchical(params: AIProductivityHierarchicalParams): Promise<any> {
    try {
      // Construct the URL with path parameters for year and month
      const url = `${ENDPOINTS.AI_PRODUCTIVITY_HIERARCHICAL}/${params.year}/${params.month}/detailed`

      // Build query parameters
      const queryParams: Record<string, string | boolean | number> = {
        rootEmployeeEmail: params.rootEmployeeEmail,
      }

      // Add isDeveloper only if it's defined (not for "All" selection)
      if (params.isDeveloper !== undefined) {
        queryParams.isDeveloper = params.isDeveloper
      }

      // Add isFullTimeEmployee only if it's defined (not for "All" selection)
      if (params.isFullTimeEmployee !== undefined) {
        queryParams.isFullTimeEmployee = params.isFullTimeEmployee
      }

      // Add level parameter for lazy loading (default: 1)
      if (params.level !== undefined) {
        queryParams.level = params.level
      }

      setProgramFilters(queryParams, params.programIds, params.programAnalyticsType)

      const response = await get(url, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching AI Productivity Hierarchical data:', error)
      throw error
    }
  }

  /**
   * Fetch AI Productivity Monthly Trend Range data for a given date range and root employee email
   * URL format: /agentic-analytics/v2/monthly-trend-range?rootEmployeeEmail={email}&startDate={YYYY/MM}&endDate={YYYY/MM}
   * @param params - Parameters including startDate, endDate, rootEmployeeEmail, and optional isDeveloper
   * @returns Promise of monthly trend range AI productivity data
   */
  async getAIProductivityMonthlyTrendRange(params: AIProductivityMonthlyTrendRangeParams): Promise<any> {
    try {
      // Build query parameters
      const queryParams: Record<string, string | boolean | number> = {
        rootEmployeeEmail: params.rootEmployeeEmail,
        startDate: params.startDate,
        endDate: params.endDate,
      }

      // Add isDeveloper only if it's defined (not for "All" selection)
      if (params.isDeveloper !== undefined) {
        queryParams.isDeveloper = params.isDeveloper
      }

      // Add isFullTimeEmployee only if it's defined (not for "All" selection)
      if (params.isFullTimeEmployee !== undefined) {
        queryParams.isFullTimeEmployee = params.isFullTimeEmployee
      }

      setProgramFilters(queryParams, params.programIds, params.programAnalyticsType)

      const response = await get(ENDPOINTS.AI_PRODUCTIVITY_MONTHLY_TREND_RANGE, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching AI Productivity Monthly Trend Range data:', error)
      throw error
    }
  }

  /**
   * Fetch AI Productivity Reportees data for a given root employee email
   * URL format: /agentic-analytics/hierarchical/reportees?rootEmployeeEmail={email}&level={level}
   * @param params - Parameters including rootEmployeeEmail and optional level (default: 2)
   * @returns Promise of reportees data
   */
  async getAIProductivityReportees(params: AIProductivityReporteesParams): Promise<any> {
    try {
      // Build query parameters
      const queryParams: Record<string, string | number> = {
        rootEmployeeEmail: params.rootEmployeeEmail,
      }

      // Add level if provided, default to 2
      if (params.level !== undefined) {
        queryParams.level = params.level
      } else {
        queryParams.level = 2
      }

      const response = await get(ENDPOINTS.AI_PRODUCTIVITY_REPORTEES, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching AI Productivity Reportees data:', error)
      throw error
    }
  }

  /**
   * Update user settings (isDeveloper, isLicRevExcl, or licenseRevocationExclusions)
   * URL format: PATCH /users/{email}/update?isDeveloper={value} or ?isLicRevExcl={value}
   * @param params - Parameters including email and the setting to update
   * @returns Promise of updated user data
   */
  async updateUserSettings(params: UpdateUserSettingsParams): Promise<any> {
    try {
      // Construct the URL with email in path
      const url = `${ENDPOINTS.UPDATE_USER_SETTINGS}/${params.email}/update`

      // Build query parameters - only include the setting being updated
      const queryParams: Record<string, boolean> = {}
      if (params.isDeveloper !== undefined) {
        queryParams.isDeveloper = params.isDeveloper
      }
      if (params.isLicRevExcl !== undefined) {
        queryParams.isLicRevExcl = params.isLicRevExcl
      }

      // Build request options
      const requestOptions: any = {params: queryParams}

      // Add request body for licenseRevocationExclusions (send array directly, not wrapped in object)
      if (params.licenseRevocationExclusions !== undefined) {
        requestOptions.data = params.licenseRevocationExclusions
      }

      const response = await patch(url, requestOptions)
      return response.data
    } catch (error) {
      console.error('Error updating user settings:', error)
      throw error
    }
  }

  /**
   * Fetch AI Productivity Inactive Users data for a given year, month, and root employee email
   * URL format: /agentic-analytics/inactive-users/{year}/{month}?rootEmployeeEmail={email}&isDeveloper={value}&isFullTimeEmployee={value}
   * @param params - Parameters including year, month, rootEmployeeEmail, optional isDeveloper, and optional isFullTimeEmployee
   * @returns Promise of inactive users data
   */
  async getAIProductivityInactiveUsers(params: AIProductivityInactiveUsersParams): Promise<InactiveUsersResponse> {
    try {
      // Construct the URL with path parameters for year and month
      const url = `${ENDPOINTS.AI_PRODUCTIVITY_INACTIVE_USERS}/${params.year}/${params.month}`

      // Build query parameters
      const queryParams: Record<string, string | boolean> = {
        rootEmployeeEmail: params.rootEmployeeEmail,
      }

      // Add isDeveloper only if it's defined (not for "All" selection)
      if (params.isDeveloper !== undefined) {
        queryParams.isDeveloper = params.isDeveloper
      }

      // Add isFullTimeEmployee only if it's defined (not for "All" selection)
      if (params.isFullTimeEmployee !== undefined) {
        queryParams.isFullTimeEmployee = params.isFullTimeEmployee
      }

      // Add isDetailView flag (default to false for initial summary load)
      if (params.isDetailView !== undefined) {
        queryParams.isDetailView = params.isDetailView
      }

      if (params.programIds && params.programIds.length > 0) {
        queryParams.programIds = params.programIds.join(',')
      }

      const response = await get(url, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching AI Productivity Inactive Users data:', error)
      throw error
    }
  }

  /**
   * Fetch Credit Usage data for a given year, month, and root employee email
   * URL format: /agentic-analytics/credit-usage/{year}/{month}?page=1&size=10&rootEmployeeEmail={email}
   * @param params - Parameters including year, month, rootEmployeeEmail, and optional pagination/filter params
   * @returns Promise of credit usage data
   */
  async getCreditUsage(params: CreditUsageParams): Promise<any> {
    try {
      const url = `${ENDPOINTS.AI_PRODUCTIVITY_CREDIT_USAGE}/${params.year}/${params.month}`

      const queryParams = {
        rootEmployeeEmail: params.rootEmployeeEmail,
        ...(params.page !== undefined && {page: params.page}),
        ...(params.size !== undefined && {size: params.size}),
        ...(params.isDetailView !== undefined && {isDetailView: params.isDetailView}),
        ...(params.toolTypes?.length && {toolTypes: params.toolTypes.join(',')}),
        ...(params.isDeveloper !== undefined && {isDeveloper: params.isDeveloper}),
        ...(params.isFullTimeEmployee !== undefined && {isFullTimeEmployee: params.isFullTimeEmployee}),
        ...(params.programIds?.length && {programIds: params.programIds.join(',')}),
        ...(params.programAnalyticsType !== undefined && {'program-analytics-type': params.programAnalyticsType}),
      }

      const response = await get(url, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching Credit Usage data:', error)
      throw error
    }
  }

  /**
   * Fetch User Hierarchy data
   * URL format: /users/hierarchy (email parameter is optional)
   * @param params - Parameters including optional email
   * @returns Promise of user hierarchy data
   */
  async getUserHierarchy(params: UserHierarchyParams = {}): Promise<any> {
    try {
      // Build query parameters - only include email if provided
      const queryParams: Record<string, string> = {}

      if (params.email) {
        queryParams.email = params.email
      }

      const response = await get(ENDPOINTS.USER_HIERARCHY, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching User Hierarchy data:', error)
      throw error
    }
  }

  /**
   * Fetch AI Performer data for top performers
   * URL format: /agentic-analytics/v2/ai-performer/{month}?toolTypes={types}&selectedTab={tab}
   * @param params - Parameters including month (1-12) and optional query parameters
   * @returns Promise of AI performer data
   */
  async getAIPerformer(params: AIPerformerParams): Promise<any> {
    try {
      // Construct the URL with path parameters for year and month
      const url = `${ENDPOINTS.AI_PERFORMER}/${params.year}/${params.month}`

      // Build query parameters - only send toolTypes and selectedTab
      const queryParams: Record<string, string> = {}

      if (params.toolTypes) {
        queryParams.toolTypes = params.toolTypes
      }

      if (params.selectedTab) {
        queryParams.selectedTab = params.selectedTab
      }

      const response = await get(url, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching AI Performer data:', error)
      throw error
    }
  }

  /**
   * Fetch Credit Usage Monthly Trend data
   * URL format: /agentic-analytics/v2/credit-usage-monthly-trend?rootEmployeeEmail={email}&isDeveloper={value}&startDate={date}&endDate={date}&isFullTimeEmployee={value}
   * @param params - Parameters including rootEmployeeEmail and optional filters
   * @returns Promise of credit usage monthly trend data
   */
  async getCreditUsageMonthlyTrend(params: CreditUsageMonthlyTrendParams): Promise<any> {
    try {
      const queryParams: Record<string, string | boolean | number> = {
        rootEmployeeEmail: params.rootEmployeeEmail,
      }
      if (params.isDeveloper !== undefined) {
        queryParams.isDeveloper = params.isDeveloper
      }
      if (params.startDate) {
        queryParams.startDate = params.startDate
      }
      if (params.endDate) {
        queryParams.endDate = params.endDate
      }
      if (params.isFullTimeEmployee !== undefined) {
        queryParams.isFullTimeEmployee = params.isFullTimeEmployee
      }
      setProgramFilters(queryParams, params.programIds, params.programAnalyticsType)
      const response = await get(ENDPOINTS.AI_CREDIT_USAGE_MONTHLY_TREND, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching Credit Usage Monthly Trend data:', error)
      throw error
    }
  }

  /**
   * Fetch LOC Usage Monthly Trend data
   * URL format: /agentic-analytics/v2/loc-usage-monthly-trend?rootEmployeeEmail={email}&isDeveloper={value}&startDate={date}&endDate={date}&isFullTimeEmployee={value}&toolTypes={toolTypes}
   * @param params - Parameters including rootEmployeeEmail, toolTypes and optional filters
   * @returns Promise of LOC usage monthly trend data
   */
  async getLocUsageMonthlyTrend(params: LocUsageMonthlyTrendParams): Promise<any> {
    try {
      // Build query parameters with proper type
      const queryParams: Record<string, string | boolean | number> = {
        rootEmployeeEmail: params?.rootEmployeeEmail,
        toolTypes: params?.toolTypes?.join(','),
      }
      if (params.isDeveloper !== undefined) {
        queryParams.isDeveloper = params.isDeveloper
      }
      if (params.startDate) {
        queryParams.startDate = params.startDate
      }
      if (params.endDate) {
        queryParams.endDate = params.endDate
      }
      if (params.isFullTimeEmployee !== undefined) {
        queryParams.isFullTimeEmployee = params.isFullTimeEmployee
      }
      setProgramFilters(queryParams, params.programIds, params.programAnalyticsType)
      const response = await get(ENDPOINTS.AI_LOC_USAGE_MONTHLY_TREND, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching LOC Usage Monthly Trend data:', error)
      throw error
    }
  }

  /**
   * Fetch AI LOC Benchmark data for a given year and month
   * URL format: /agentic-analytics/ai-loc-benchmark/{year}/{month}?page={page}&size={size}&email={email}&isFullTimeEmployee={value}&isDeveloper={value}&programIds={ids}&program-analytics-type={type}
   * @param params - Parameters including year, month, page, size, and optional filters
   * @returns Promise of AI LOC Benchmark data
   */
  async getAILoCBenchmark(params: AILoCBenchmarkParams): Promise<AILoCBenchmarkResponse> {
    const url = `${ENDPOINTS.AI_LOC_BENCHMARK}/${params.year}/${params.month}`

    const queryParams: Record<string, string | boolean | number> = {
      page: params.page ?? 1,
      size: params.size ?? 15,
    }

    if (params.rootEmployeeEmail) {
      queryParams.email = params.rootEmployeeEmail
    }

    if (params.isFullTimeEmployee !== undefined) {
      queryParams.isFullTimeEmployee = params.isFullTimeEmployee
    }

    if (params.isDeveloper !== undefined) {
      queryParams.isDeveloper = params.isDeveloper
    }

    setProgramFilters(queryParams, params.programIds, params.programAnalyticsType)

    const response = await get(url, {params: queryParams})
    return response.data
  }

  private scopeQuery(params: AIEmployeeScopeParams): QueryParams {
    const query: QueryParams = {email: params.email}
    if (params.employeeType !== undefined) query['employee-type'] = params.employeeType
    if (params.role !== undefined) query.role = params.role
    setProgramFilters(query, params.programIds, params.programAnalyticsType)
    return query
  }

  private toolQuery({
    email,
    tool,
    programIds,
    programAnalyticsType,
  }: Pick<AIToolPeriodParams, 'email' | 'tool' | 'programIds' | 'programAnalyticsType'>): QueryParams {
    const query: QueryParams = {email, ...(tool !== undefined && {tool})}
    setProgramFilters(query, programIds, programAnalyticsType)
    return query
  }

  private async getBlob(url: string, params: QueryParams): Promise<Blob> {
    const response = await get(url, {params, responseType: 'blob'})
    return response.data
  }

  /**
   * Paginated inactive users list
   * URL format: /agentic-analytics/{year}/{month}/inactive-users?email&employee-type&role&tool&page&size
   */
  async getInactiveUsersList(params: AIInactiveUsersListParams): Promise<InactiveUsersListResponse> {
    const response = await get(periodUrl(params.year, params.month, 'inactive-users'), {
      params: {
        ...this.scopeQuery(params),
        ...(params.tool !== undefined && {tool: params.tool}),
        page: params.page ?? 0,
        size: params.size ?? 100,
      },
    })
    return response.data
  }

  /**
   * Download inactive users report
   * URL format: /agentic-analytics/inactive-users/{year}/{month}/report?email&tool
   */
  downloadInactiveUsersReport(params: AIToolPeriodParams): Promise<Blob> {
    return this.getBlob(
      `${ENDPOINTS.AI_INACTIVE_USERS_REPORT}/${params.year}/${params.month}/report`,
      this.toolQuery(params),
    )
  }

  /**
   * User search (filter API)
   * URL format: /agentic-analytics/user-search?employee-type&role&email
   */
  async searchUsers(params: AIUserSearchParams): Promise<any> {
    const response = await get(ENDPOINTS.AI_USER_SEARCH, {params: this.scopeQuery(params)})
    return response.data
  }

  /**
   * Team hierarchy overview report download
   * URL format: /agentic-analytics/{year}/{month}/team-overview-report?employee-type&role&email
   */
  downloadTeamOverviewReport(params: AIPeriodScopeParams): Promise<Blob> {
    return this.getBlob(periodUrl(params.year, params.month, 'team-overview-report'), this.scopeQuery(params))
  }

  /**
   * Lines of code report download
   * URL format: /agentic-analytics/{year}/{month}/loc-report?employee-type&role&email
   */
  downloadLocReport(params: AIPeriodScopeParams): Promise<Blob> {
    return this.getBlob(periodUrl(params.year, params.month, 'loc-report'), this.scopeQuery(params))
  }

  /**
   * Metrics overview (inactive + license specific data)
   * URL format: /agentic-analytics/{year}/{month}/tool-metrics?email&tool
   */
  async getToolMetrics(params: AIToolPeriodParams): Promise<ToolMetricsResponse> {
    const response = await get(periodUrl(params.year, params.month, 'tool-metrics'), {
      params: this.toolQuery(params),
    })
    return response.data
  }

  /**
   * AI adoption overview and credits overview
   * URL format: /agentic-analytics/{year}/{month}/aggregate-metrics?employee-type&role&email
   */
  async getAggregateMetrics(params: AIPeriodScopeParams): Promise<AggregateMetricsResponse> {
    const response = await get(periodUrl(params.year, params.month, 'aggregate-metrics'), {
      params: this.scopeQuery(params),
    })
    return response.data
  }

  /**
   * Fetch access delegations audit history
   * URL format: /api/access-delegations/audit?capability={capability}&page={page}&size={size}
   * @param params - Parameters including capability, page, and size
   * @returns Promise of audit history data
   */
  async getAccessDelegationsAudit(params: AccessDelegationsAuditParams): Promise<any> {
    try {
      const queryParams: Record<string, string | number> = {
        capability: params.capability,
        page: params.page,
        size: params.size,
      }

      if (params.startDate) {
        queryParams.startDate = params.startDate
      }
      if (params.endDate) {
        queryParams.endDate = params.endDate
      }
      if (params.search) {
        queryParams.search = params.search
      }

      const response = await get(ENDPOINTS.ACCESS_DELEGATIONS_AUDIT, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching access delegations audit:', error)
      throw error
    }
  }

  /**
   * Fetch access delegations candidates list
   * URL format: /api/access-delegations/candidates?search={search}&page={page}&size={size}
   * @param params - Parameters including page, size, and optional search
   * @returns Promise of candidates data
   */
  async getAccessDelegationsCandidates(params: AccessDelegationsCandidatesParams): Promise<any> {
    try {
      const queryParams: Record<string, string | number> = {
        page: params.page,
        size: params.size,
      }

      if (params.search) {
        queryParams.search = params.search
      }

      const response = await get(ENDPOINTS.ACCESS_DELEGATIONS_CANDIDATES, {params: queryParams})
      return response.data
    } catch (error) {
      console.error('Error fetching access delegations candidates:', error)
      throw error
    }
  }

  /**
   * Grant access delegations for one or more users
   * URL format: POST /api/access-delegations
   * @param delegateEmails - Array of email addresses to grant delegation
   * @returns Promise of the response data
   */
  async updateAccessDelegations(delegateEmails: string[]): Promise<any> {
    try {
      const response = await post(ENDPOINTS.ACCESS_DELEGATIONS, {
        data: {delegateEmails},
      })
      return response.data
    } catch (error) {
      console.error('Error updating access delegations:', error)
      throw error
    }
  }

  /**
   * Revoke access delegations for one or more users
   * URL format: PATCH /api/access-delegations/revoke
   * @param delegationIds - Array of delegation IDs to revoke
   * @returns Promise of the response data
   */
  async revokeAccessDelegations(delegationIds: string[]): Promise<any> {
    try {
      const response = await patch(ENDPOINTS.ACCESS_DELEGATIONS_REVOKE, {
        data: {delegationIds},
      })
      return response.data
    } catch (error) {
      console.error('Error revoking access delegations:', error)
      throw error
    }
  }

  /**
   * Fetch access delegations summary for the current user
   * URL format: GET /api/access-delegations/access-summary
   * @returns Promise of access summary data including delegation contexts
   */
  async getAccessDelegationsSummary(): Promise<any> {
    try {
      const response = await get(ENDPOINTS.ACCESS_DELEGATIONS_SUMMARY)
      return response.data
    } catch (error) {
      console.error('Error fetching access delegations summary:', error)
      throw error
    }
  }
}

// Export singleton instance
export const aiMetricsService = new AIMetricsService()
export default aiMetricsService
